import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  fetchIcStatistics,
  fetchEdStatistics,
  fetchClStatistics,
  fetchMissionStatistics,
  fetchBillingStatistics,
  fetchFaeStatistics,
  fetchRatioStatistics,
} from "./queries";
import { QUERY_STATUS } from "constants/constants";

const DashboardStatsContext = createContext();

export const useDashboardStatsContext = () => useContext(DashboardStatsContext);

export const DashboardStatsProvider = (props) => {
  const [state, setState] = useState({
    dashboardStats: {},
    loadDataStatus: {},
  });

  const updateState = useCallback(
    (statsKey, update) =>
      setState((prevState) => ({
        ...prevState,
        dashboardStats: {
          ...prevState.dashboardStats,
          [statsKey]: update.dashboardStats,
        },
        loadDataStatus: {
          ...prevState.loadDataStatus,
          [statsKey]: update.loadDataStatus,
        },
      })),
    []
  );

  const fetchStatisticsHandler = async (fetchFunction, params, statsKey) => {
    try {
      updateState(statsKey, { loadDataStatus: QUERY_STATUS.LOADING });
      const data = await fetchFunction(params);
      updateState(statsKey, {
        dashboardStats: data,
        loadDataStatus: QUERY_STATUS.SUCCESS,
      });
    } catch (e) {
      updateState(statsKey, { loadDataStatus: QUERY_STATUS.ERROR });
      console.error("fetchStatisticsHandler", e);
    }
  };

  const statsGetters = useMemo(
    () => ({
      dashboardIcStatsGetter: (params) =>
        fetchStatisticsHandler(fetchIcStatistics, params, "icStats"),
      dashboardEdStatsGetter: (params) =>
        fetchStatisticsHandler(fetchEdStatistics, params, "edStats"),
      dashboardClStatsGetter: (params) =>
        fetchStatisticsHandler(fetchClStatistics, params, "clStats"),
      dashboardMissionStatsGetter: (params) =>
        fetchStatisticsHandler(fetchMissionStatistics, params, "missionStats"),
      dashboardBillingStatsGetter: (params) =>
        fetchStatisticsHandler(fetchBillingStatistics, params, "billingStats"),
      dashboardFaeStatsGetter: (params) =>
        fetchStatisticsHandler(fetchFaeStatistics, params, "faeStats"),
      dashboardRatioStatsGetter: (params) =>
        fetchStatisticsHandler(fetchRatioStatistics, params, "ratioStats"),
    }),
    [updateState]
  );

  const values = useMemo(
    () => ({
      ...state,
      ...statsGetters,
    }),
    [state, statsGetters]
  );

  return <DashboardStatsContext.Provider value={values} {...props} />;
};
